/**
 * Broker Priority Tab Component
 * 
 * Compact, modern interface for managing broker_data_priority table.
 * Features:
 * - View priority matrix organized by Exchange × Instrument Type
 * - Drag-and-drop to reorder broker priority within each cell
 * - Add brokers from active brokers list (from BIS)
 * - Add new Exchange/Instrument Type rows
 * - Delete individual broker entries or entire rows
 * - Real-time save with visual feedback
 * 
 * @module InstrumentMaster/tabs/BrokerPriorityTab
 */

import React, { useState, useEffect, useCallback } from 'react';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { 
  FiPlus, 
  FiTrash2, 
  FiRefreshCw,
  FiMove,
  FiChevronDown,
  FiChevronRight,
  FiX,
  FiCheck
} from 'react-icons/fi';

// Available exchanges and instrument types
const EXCHANGES = ['NSE', 'BSE', 'NFO', 'BFO', 'MCX', 'CDS', 'NCDEX'];
const INSTRUMENT_TYPES = ['EQUITY', 'FUTURE', 'OPTION', 'COMMODITY', 'CURRENCY', 'INDEX'];

const BrokerPriorityTab = () => {
  const { showSnackbar } = useSnackbar();
  
  // State
  const [loading, setLoading] = useState(true);
  const [priorityMatrix, setPriorityMatrix] = useState({});
  const [availableBrokers, setAvailableBrokers] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [dragState, setDragState] = useState({ isDragging: false, source: null });
  
  // Modal state
  const [showAddRowForm, setShowAddRowForm] = useState(false);
  const [newRowExchange, setNewRowExchange] = useState('');
  const [newRowInstrumentType, setNewRowInstrumentType] = useState('');
  
  // Add broker popover state
  const [addBrokerPopover, setAddBrokerPopover] = useState({ open: false, exchange: null, instrumentType: null });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [matrixResponse, brokersResponse] = await Promise.all([
        services.instruments.admin.getPriorityMatrix(),
        services.brokerPlatforms.getAvailableBrokers()
      ]);

      if (matrixResponse.data) {
        const rawMatrix = matrixResponse.data.matrix || matrixResponse.data.data?.matrix || {};

        // Normalize and sort priority arrays to ensure unique, numeric ranks and stable ordering
        const matrixData = {};
        Object.entries(rawMatrix).forEach(([exchange, types]) => {
          matrixData[exchange] = {};
          Object.entries(types || {}).forEach(([instrumentType, arr]) => {
            const normalized = (arr || []).map(item => ({
              ...item,
              priority_rank: Number(item.priority_rank || 0),
              broker_id: Number(item.broker_id ?? item.id ?? 0)
            }));
            normalized.sort((a, b) => (a.priority_rank || 0) - (b.priority_rank || 0));
            matrixData[exchange][instrumentType] = normalized;
          });
        });

        setPriorityMatrix(matrixData);
        const expanded = {};
        Object.keys(matrixData).forEach(exchange => { expanded[exchange] = true; });
        setExpandedSections(expanded);
      }

      if (brokersResponse.data) {
        const brokerList = Array.isArray(brokersResponse.data) 
          ? brokersResponse.data 
          : brokersResponse.data.brokers || brokersResponse.data.data || [];
        console.log('[BrokerPriority] Raw broker response:', brokersResponse.data);
        console.log('[BrokerPriority] Parsed broker list:', brokerList);
        const activeBrokers = brokerList.filter(b => b.is_active !== false);
        console.log('[BrokerPriority] Active brokers:', activeBrokers);
        setAvailableBrokers(activeBrokers);
      } else {
        console.log('[BrokerPriority] No broker data in response');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar(error.message || 'Failed to load priority configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (exchange) => {
    setExpandedSections(prev => ({ ...prev, [exchange]: !prev[exchange] }));
  };

  const getAvailableBrokersForCell = useCallback((exchange, instrumentType) => {
    const cellBrokers = priorityMatrix[exchange]?.[instrumentType] || [];
    const usedBrokerIds = cellBrokers.map(b => b.broker_id);
    // Brokers from BIS use broker_id, not id
    return availableBrokers.filter(broker => {
      const bId = broker.broker_id || broker.id;
      return !usedBrokerIds.includes(bId);
    });
  }, [priorityMatrix, availableBrokers]);

  // Helper to get display name from available brokers (maps broker_id to display_name)
  const getBrokerDisplayName = useCallback((brokerId, fallbackName) => {
    // Brokers from BIS use broker_id, not id
    const broker = availableBrokers.find(b => (b.broker_id || b.id) === brokerId);
    return broker?.display_name || broker?.name || fallbackName || `Broker ${brokerId}`;
  }, [availableBrokers]);

  const addBrokerToCell = async (exchange, instrumentType, brokerId) => {
    console.log('[BrokerPriority] addBrokerToCell called:', { exchange, instrumentType, brokerId });
    
    // Close popover immediately for better UX
    setAddBrokerPopover({ open: false, exchange: null, instrumentType: null });
    
    const parsedId = typeof brokerId === 'string' ? parseInt(brokerId, 10) : brokerId;
    // Brokers from BIS use broker_id, not id
    const broker = availableBrokers.find(b => (b.broker_id || b.id) === parsedId);
    console.log('[BrokerPriority] Found broker:', broker, 'parsedId:', parsedId);
    
    if (!broker) {
      console.error('[BrokerPriority] Broker not found for id:', parsedId);
      showSnackbar('Broker not found', 'error');
      return;
    }

    const cellBrokers = priorityMatrix[exchange]?.[instrumentType] || [];
    // New broker gets max rank (lowest priority) - user can drag to reorder
    const newRank = cellBrokers.length + 1;
    const brokerDisplayName = String(broker.display_name || broker.name || `Broker ${broker.id}`).substring(0, 50);

    // Prepare payload for API - brokers from BIS use broker_id field
    const actualBrokerId = broker.broker_id || broker.id;
    const payload = {
      exchange: String(exchange).toUpperCase(),
      instrument_type: String(instrumentType).toUpperCase(),
      broker_id: Number(actualBrokerId),
      broker_name: brokerDisplayName,
      priority_rank: Number(newRank),
      is_active: true
    };
    
    console.log('[BrokerPriority] Creating priority rule with payload:', JSON.stringify(payload, null, 2));

    try {
      const response = await services.instruments.admin.createPriorityRule(payload);
      console.log('[BrokerPriority] API response:', response);

      if (response.data) {
        // Add to local state - use actualBrokerId for consistency
        // Use priority_rank returned by API when available to avoid rank collisions
        const returnedRank = response.data.priority_rank !== undefined
          ? Number(response.data.priority_rank)
          : newRank;

        const newBroker = {
          broker_id: actualBrokerId,
          broker_name: brokerDisplayName,
          priority_rank: returnedRank,
          priority_id: response.data.priority_id || response.data.id,
          is_active: true
        };

        setPriorityMatrix(prev => {
          const updated = { ...prev };
          const cell = [...(updated[exchange]?.[instrumentType] || []), newBroker];
          // Ensure cell is sorted by numeric rank after insert
          cell.sort((a, b) => (a.priority_rank || 0) - (b.priority_rank || 0));
          updated[exchange] = { ...updated[exchange], [instrumentType]: cell };
          return updated;
        });

        showSnackbar(`Added ${brokerDisplayName}`, 'success');
      } else {
        showSnackbar('Broker added but no response data', 'warning');
      }
    } catch (error) {
      console.error('[BrokerPriority] Error adding broker:', error);
      console.error('[BrokerPriority] Error response:', error.response?.data);
      
      // Extract a clean string error message
      let errorMessage = 'Failed to add broker';
      try {
        if (error.response?.data) {
          const errData = error.response.data;
          if (typeof errData === 'string') {
            errorMessage = errData;
          } else if (errData.detail) {
            if (Array.isArray(errData.detail)) {
              // FastAPI validation errors - extract just the msg field
              const messages = errData.detail
                .map(d => {
                  if (typeof d === 'string') return d;
                  if (d.msg) return String(d.msg);
                  if (d.message) return String(d.message);
                  return 'Validation error';
                })
                .filter(Boolean);
              errorMessage = messages.join('; ') || 'Validation failed';
            } else if (typeof errData.detail === 'string') {
              errorMessage = errData.detail;
            } else if (typeof errData.detail === 'object') {
              errorMessage = errData.detail.msg || errData.detail.message || 'Request failed';
            }
          } else if (errData.message && typeof errData.message === 'string') {
            errorMessage = errData.message;
          }
        } else if (error.message && typeof error.message === 'string') {
          errorMessage = error.message;
        }
      } catch (parseErr) {
        console.error('[BrokerPriority] Error parsing error message:', parseErr);
        errorMessage = 'Failed to add broker - unknown error';
      }
      
      // Ensure errorMessage is always a string
      console.log('[BrokerPriority] Final error message:', errorMessage, typeof errorMessage);
      showSnackbar(String(errorMessage).substring(0, 200), 'error');
    }
  };

  const removeBrokerFromCell = async (exchange, instrumentType, brokerIndex) => {
    const cellBrokers = priorityMatrix[exchange]?.[instrumentType] || [];
    const brokerToRemove = cellBrokers[brokerIndex];
    
    if (!brokerToRemove?.priority_id) {
      showSnackbar('Cannot remove: missing priority ID', 'error');
      return;
    }

    try {
      await services.instruments.admin.deletePriorityRule(brokerToRemove.priority_id);

      const updatedBrokers = cellBrokers.filter((_, idx) => idx !== brokerIndex);
      updatedBrokers.forEach((broker, idx) => { broker.priority_rank = idx + 1; });

      setPriorityMatrix(prev => ({
        ...prev,
        [exchange]: { ...prev[exchange], [instrumentType]: updatedBrokers }
      }));

      if (updatedBrokers.length > 0) {
        await services.instruments.admin.bulkUpdatePriorities(
          updatedBrokers.map(b => ({ priority_id: b.priority_id, priority_rank: b.priority_rank }))
        );
      }

      showSnackbar(`Removed ${brokerToRemove.broker_name}`, 'success');
    } catch (error) {
      console.error('Error removing broker:', error);
      showSnackbar(error.message || 'Failed to remove broker', 'error');
    }
  };

  /**
   * Toggle broker active status - Fixed logic to use explicit boolean check
   */
  const toggleBrokerActive = async (exchange, instrumentType, brokerIndex) => {
    const cellBrokers = priorityMatrix[exchange]?.[instrumentType] || [];
    const broker = cellBrokers[brokerIndex];
    
    if (!broker?.priority_id) return;

    // Determine current state explicitly (default to true if undefined)
    const currentlyActive = broker.is_active === true || broker.is_active === undefined;
    const newActiveState = !currentlyActive;

    try {
      await services.instruments.admin.updatePriorityRule(broker.priority_id, {
        is_active: newActiveState
      });

      // Update local state with explicit boolean
      setPriorityMatrix(prev => {
        const updated = JSON.parse(JSON.stringify(prev)); // Deep clone
        updated[exchange][instrumentType][brokerIndex].is_active = newActiveState;
        return updated;
      });

      showSnackbar(
        `${broker.broker_name} ${newActiveState ? 'enabled' : 'disabled'}`,
        'success'
      );
    } catch (error) {
      console.error('Error toggling broker:', error);
      showSnackbar(error.message || 'Failed to update broker status', 'error');
    }
  };

  // Drag handlers
  const handleDragStart = (e, exchange, instrumentType, brokerIndex) => {
    setDragState({ isDragging: true, source: { exchange, instrumentType, brokerIndex } });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ exchange, instrumentType, brokerIndex }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, exchange, instrumentType, targetIndex) => {
    e.preventDefault();
    console.log('[BrokerPriority] handleDrop:', { exchange, instrumentType, targetIndex, dragState });
    if (!dragState.source) return;
    
    const { exchange: srcExchange, instrumentType: srcType, brokerIndex: srcIndex } = dragState.source;
    
    if (srcExchange !== exchange || srcType !== instrumentType || srcIndex === targetIndex) {
      console.log('[BrokerPriority] Drop cancelled - same position or different section');
      setDragState({ isDragging: false, source: null });
      return;
    }

    const cellBrokers = [...(priorityMatrix[exchange]?.[instrumentType] || [])];
    const [movedBroker] = cellBrokers.splice(srcIndex, 1);
    cellBrokers.splice(targetIndex, 0, movedBroker);
    cellBrokers.forEach((broker, idx) => { broker.priority_rank = idx + 1; });

    console.log('[BrokerPriority] New order:', cellBrokers.map(b => ({ name: b.broker_name, rank: b.priority_rank, id: b.priority_id })));

    setPriorityMatrix(prev => ({
      ...prev,
      [exchange]: { ...prev[exchange], [instrumentType]: cellBrokers }
    }));

    setDragState({ isDragging: false, source: null });

    try {
      const updates = cellBrokers.map(b => ({ priority_id: b.priority_id, priority_rank: b.priority_rank }));
      console.log('[BrokerPriority] Sending bulk update:', updates);
      await services.instruments.admin.bulkUpdatePriorities(updates);
      showSnackbar('Priority order updated', 'success');
    } catch (error) {
      console.error('[BrokerPriority] Error updating priority order:', error);
      showSnackbar(error.message || 'Failed to save new order', 'error');
    }
  };

  const handleDragEnd = () => {
    setDragState({ isDragging: false, source: null });
  };

  const addNewRow = async () => {
    if (!newRowExchange || !newRowInstrumentType) {
      showSnackbar('Please select both Exchange and Instrument Type', 'warning');
      return;
    }

    if (priorityMatrix[newRowExchange]?.[newRowInstrumentType]?.length > 0) {
      showSnackbar('This combination already exists', 'warning');
      return;
    }

    setPriorityMatrix(prev => ({
      ...prev,
      [newRowExchange]: { ...prev[newRowExchange], [newRowInstrumentType]: [] }
    }));

    setExpandedSections(prev => ({ ...prev, [newRowExchange]: true }));
    setShowAddRowForm(false);
    setNewRowExchange('');
    setNewRowInstrumentType('');
    showSnackbar(`Added ${newRowExchange} ${newRowInstrumentType}`, 'success');
  };

  const deleteRow = async (exchange, instrumentType) => {
    const cellBrokers = priorityMatrix[exchange]?.[instrumentType] || [];
    
    if (cellBrokers.length > 0 && !window.confirm(`Delete ${cellBrokers.length} broker rules for ${exchange} ${instrumentType}?`)) {
      return;
    }

    try {
      for (const broker of cellBrokers) {
        if (broker.priority_id) {
          await services.instruments.admin.deletePriorityRule(broker.priority_id);
        }
      }

      setPriorityMatrix(prev => {
        const updated = { ...prev };
        if (updated[exchange]) {
          delete updated[exchange][instrumentType];
          if (Object.keys(updated[exchange]).length === 0) delete updated[exchange];
        }
        return updated;
      });

      showSnackbar(`Deleted ${exchange} ${instrumentType}`, 'success');
    } catch (error) {
      console.error('Error deleting row:', error);
      showSnackbar(error.message || 'Failed to delete row', 'error');
    }
  };

  // Helper to check if broker is active
  const isBrokerActive = (broker) => broker.is_active === true || broker.is_active === undefined;

  // Get rank badge style
  const getRankBadge = (rank, isActive) => {
    if (!isActive) return 'bg-bg-3 text-fg-3';
    if (rank === 1) return 'bg-green-500 text-white';
    if (rank === 2) return 'bg-blue-500 text-white';
    if (rank === 3) return 'bg-amber-500 text-white';
    return 'bg-gray-400 text-white';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <FiRefreshCw className="animate-spin text-2xl text-blue-500 mr-2" />
        <span className="text-fg-2">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Compact Header */}
      <div className="flex items-center justify-between p-3 bg-white dark:bg-bg-1 rounded-lg border border-[var(--divider)]">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-fg-2">Broker Data Priority</span>
          <span className="text-xs text-fg-3">
            {Object.values(priorityMatrix).reduce((acc, types) => acc + Object.values(types).flat().length, 0)} rules
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAddRowForm(true)}
            className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
          >
            <FiPlus className="text-sm" />
            Add Section
          </button>
          <button
            onClick={fetchData}
            className="p-1.5 text-fg-2 hover:text-fg-2 dark:text-fg-3 dark:hover:text-gray-200 hover:bg-bg-3 rounded-md transition-colors"
            title="Refresh"
          >
            <FiRefreshCw className="text-sm" />
          </button>
        </div>
      </div>

      {/* Add Row Modal */}
      {showAddRowForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddRowForm(false)}>
          <div className="bg-bg-2 rounded-lg p-4 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-fg-1">Add Priority Section</h3>
              <button onClick={() => setShowAddRowForm(false)} className="text-fg-3 hover:text-fg-2">
                <FiX />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-fg-2 mb-1">Exchange</label>
                <select
                  value={newRowExchange}
                  onChange={(e) => setNewRowExchange(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-[var(--divider)] rounded-md bg-white dark:bg-bg-3 text-fg-1"
                >
                  <option value="">Select...</option>
                  {EXCHANGES.map(ex => <option key={ex} value={ex}>{ex}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-fg-2 mb-1">Instrument Type</label>
                <select
                  value={newRowInstrumentType}
                  onChange={(e) => setNewRowInstrumentType(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-[var(--divider)] rounded-md bg-white dark:bg-bg-3 text-fg-1"
                >
                  <option value="">Select...</option>
                  {INSTRUMENT_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setShowAddRowForm(false)}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={addNewRow}
                className="btn btn-primary btn-sm"
              >
                Add Section
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Priority Matrix */}
      {Object.keys(priorityMatrix).length === 0 ? (
        <div className="text-center py-8 text-fg-3">
          <p className="text-sm">No priority rules configured.</p>
          <p className="text-xs mt-1">Click "Add Section" to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(priorityMatrix).sort().map(([exchange, instrumentTypes]) => (
            <div key={exchange} className="bg-white dark:bg-bg-1 rounded-lg border border-[var(--divider)] overflow-visible">
              {/* Exchange Header - Compact */}
              <button
                onClick={() => toggleSection(exchange)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gradient-to-r from-slate-600 to-slate-700 dark:from-slate-700 dark:to-slate-800 text-white hover:from-slate-700 hover:to-slate-800 transition-all"
              >
                <div className="flex items-center gap-2">
                  {expandedSections[exchange] ? <FiChevronDown className="text-xs" /> : <FiChevronRight className="text-xs" />}
                  <span className="text-sm font-semibold">{exchange}</span>
                  <span className="text-xs opacity-60">{Object.keys(instrumentTypes).length} types</span>
                </div>
                <span className="text-xs opacity-60">{Object.values(instrumentTypes).flat().length} brokers</span>
              </button>

              {/* Instrument Types */}
              {expandedSections[exchange] && (
                <div className="p-2 space-y-2 overflow-visible">
                  {Object.entries(instrumentTypes).sort().map(([instrumentType, brokers]) => {
                    const availableToAdd = getAvailableBrokersForCell(exchange, instrumentType);
                    const isPopoverOpen = addBrokerPopover.open && 
                      addBrokerPopover.exchange === exchange && 
                      addBrokerPopover.instrumentType === instrumentType;
                    
                    return (
                      <div key={`${exchange}_${instrumentType}`} className="border border-[var(--divider)] rounded-md overflow-visible">
                        {/* Instrument Type Header - Very Compact */}
                        <div className="flex items-center justify-between px-2.5 py-1.5 bg-bg-3/50">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-fg-2">{instrumentType}</span>
                            <span className="text-[10px] text-fg-3 bg-bg-3 px-1.5 py-0.5 rounded">
                              {brokers.length}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-1 relative">
                            {/* Delete Row */}
                            <button
                              onClick={() => deleteRow(exchange, instrumentType)}
                              className="p-1 text-fg-3 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                              title="Delete section"
                            >
                              <FiTrash2 className="text-xs" />
                            </button>
                          </div>
                        </div>

                        {/* Broker List - Compact Cards */}
                        <div className="p-2 overflow-visible">
                          {brokers.length === 0 && (
                            <div className="text-center py-3 text-fg-3 text-[10px]">
                              No brokers configured. Click + to add.
                            </div>
                          )}
                          
                          {/* Broker Cards + Add Button - All inline */}
                          <div className="flex flex-wrap gap-1.5 items-center">
                            {brokers.map((broker, index) => {
                              const isActive = isBrokerActive(broker);
                              
                              return (
                                <div
                                  key={broker.priority_id || `${broker.broker_id}_${index}`}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, exchange, instrumentType, index)}
                                  onDragOver={handleDragOver}
                                  onDrop={(e) => handleDrop(e, exchange, instrumentType, index)}
                                  onDragEnd={handleDragEnd}
                                  className={`
                                    group flex items-center gap-1.5 pl-1 pr-1.5 py-1 rounded-md border transition-all cursor-move
                                    ${isActive 
                                      ? 'bg-bg-2 border-[var(--divider)] hover:border-blue-300 dark:hover:border-blue-600' 
                                      : 'bg-bg-3/50 border-[var(--divider)] opacity-60'
                                    }
                                    ${dragState.isDragging && dragState.source?.exchange === exchange && 
                                      dragState.source?.instrumentType === instrumentType && 
                                      dragState.source?.brokerIndex === index ? 'opacity-40 scale-95' : ''}
                                  `}
                                >
                                  {/* Drag Handle */}
                                  <FiMove className="text-[10px] text-fg-3 cursor-grab" />
                                  
                                  {/* Rank Badge */}
                                  <span className={`text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded ${getRankBadge(broker.priority_rank, isActive)}`}>
                                    {broker.priority_rank}
                                  </span>
                                  
                                  {/* Broker Name - Use display name from available brokers */}
                                  <span className={`text-xs font-medium ${isActive ? 'text-fg-2' : 'text-fg-3'}`}>
                                    {getBrokerDisplayName(broker.broker_id, broker.broker_name)}
                                  </span>
                                  
                                  {/* Actions - Show on hover */}
                                  <div className="flex items-center gap-0.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {/* Toggle Active */}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleBrokerActive(exchange, instrumentType, index); }}
                                      className={`p-0.5 rounded transition-colors ${
                                        isActive 
                                          ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20' 
                                          : 'text-fg-3 hover:bg-bg-3'
                                      }`}
                                      title={isActive ? 'Disable' : 'Enable'}
                                    >
                                      {isActive ? <FiCheck className="text-[10px]" /> : <FiX className="text-[10px]" />}
                                    </button>
                                    
                                    {/* Remove */}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); removeBrokerFromCell(exchange, instrumentType, index); }}
                                      className="p-0.5 text-fg-3 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                      title="Remove"
                                    >
                                      <FiTrash2 className="text-[10px]" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            
                            {/* Add Broker Button - Inline at the end */}
                            <div className="relative">
                              <button
                                onClick={() => {
                                  if (availableToAdd.length === 0) {
                                    showSnackbar('All available brokers are already added to this section', 'info');
                                    return;
                                  }
                                  setAddBrokerPopover(
                                    isPopoverOpen 
                                      ? { open: false, exchange: null, instrumentType: null }
                                      : { open: true, exchange, instrumentType }
                                  );
                                }}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border transition-colors ${
                                  availableToAdd.length > 0
                                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                                    : 'text-fg-3 bg-bg-3 border-[var(--divider)] cursor-not-allowed'
                                }`}
                                title={availableToAdd.length > 0 ? `Add broker (${availableToAdd.length} available)` : 'All brokers added'}
                              >
                                <FiPlus className="text-xs" />
                                {availableToAdd.length > 0 && (
                                  <span className="text-[10px]">{availableToAdd.length}</span>
                                )}
                              </button>
                            
                              {/* Add Broker Dropdown - positioned below the button */}
                              {isPopoverOpen && availableToAdd.length > 0 && (
                                <div className="absolute left-0 top-full mt-1 w-52 bg-bg-2 rounded-md shadow-lg border border-[var(--divider)] z-20">
                                  <div className="p-2 border-b border-[var(--divider)] flex items-center justify-between">
                                    <span className="text-xs font-medium text-fg-2">Select Broker</span>
                                    <button 
                                      onClick={() => setAddBrokerPopover({ open: false, exchange: null, instrumentType: null })}
                                      className="text-fg-3 hover:text-fg-2 dark:hover:text-fg-3"
                                    >
                                      <FiX className="text-xs" />
                                    </button>
                                  </div>
                                  <div className="max-h-48 overflow-y-auto py-1">
                                    {availableToAdd.map(broker => {
                                      const brokerId = broker.broker_id || broker.id;
                                      return (
                                        <button
                                          key={brokerId}
                                          onClick={() => addBrokerToCell(exchange, instrumentType, brokerId)}
                                          className="w-full text-left px-3 py-2 text-xs text-fg-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center gap-2"
                                        >
                                          <span className="w-5 h-5 rounded bg-bg-3 flex items-center justify-center text-[10px] font-medium text-fg-2">
                                            {(broker.display_name || broker.name || '?').charAt(0).toUpperCase()}
                                          </span>
                                          {broker.display_name || broker.name}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Compact Help */}
      <div className="p-2.5 bg-bg-3 dark:bg-bg-1/50 rounded-lg text-[10px] text-slate-500 dark:text-slate-400">
        <span className="font-medium">Tips:</span> Drag to reorder • 
        <span className="inline-flex items-center gap-0.5 mx-1"><span className="w-3 h-3 rounded bg-green-500 text-white text-[8px] flex items-center justify-center">1</span> Primary</span> • 
        <span className="inline-flex items-center gap-0.5 mx-1"><span className="w-3 h-3 rounded bg-blue-500 text-white text-[8px] flex items-center justify-center">2</span> Secondary</span> • 
        <span className="inline-flex items-center gap-0.5 mx-1"><span className="w-3 h-3 rounded bg-amber-500 text-white text-[8px] flex items-center justify-center">3</span> Fallback</span>
      </div>
    </div>
  );
};

export default BrokerPriorityTab;
